/**
 * Affiliate Link QR Code Modal
 * 
 * Displays a modern dot-style QR code with Nordex logo branding.
 * Uses react-qrcode-logo for customizable QR code generation.
 */

import { useRef, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  QrCodeIcon,
  DownloadIcon,
  CopyIcon,
  CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { AffiliateLink } from "../types";

import { API_BASE_URL } from "@/backend/config";

// Use the redirect service URL (via Doppler in production)
// This goes through the API to track clicks, then redirects to /invite/:slug
const REDIRECT_BASE_URL = import.meta.env.VITE_REDIRECT_BASE_URL || API_BASE_URL;

// Nordex brand color
const NORDEX_GREEN = "#2ebd85";

type AffiliateLinkQrModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link: AffiliateLink | null;
};

export const AffiliateLinkQrModal = ({
  open,
  onOpenChange,
  link,
}: AffiliateLinkQrModalProps) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<QRCode>(null);

  if (!link) return null;

  const linkUrl = `${REDIRECT_BASE_URL}/r/${link.slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleDownload = () => {
    if (qrRef.current) {
      // react-qrcode-logo provides a download method
      qrRef.current.download("png", `nordex-qr-${link.slug}`);
      toast.success("QR code downloaded");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCodeIcon className="size-5 text-primary" />
            QR Code
          </DialogTitle>
          <DialogDescription>
            Scan to open: {link.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {/* QR Code with Modern Dot Style */}
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <QRCode
              ref={qrRef}
              value={linkUrl}
              size={200}
              bgColor="#ffffff"
              fgColor="#1a1a1a"
              qrStyle="dots"
              eyeRadius={[
                { outer: 12, inner: 4 }, // Top-left eye
                { outer: 12, inner: 4 }, // Top-right eye
                { outer: 12, inner: 4 }, // Bottom-left eye
              ]}
              eyeColor={NORDEX_GREEN}
              logoImage="/favicon.png"
              logoWidth={42}
              logoHeight={42}
              logoPadding={5}
              logoPaddingStyle="circle"
              removeQrCodeBehindLogo={true}
              ecLevel="H"
              quietZone={10}
            />
          </div>

          {/* Link Display */}
          <div className="mt-4 w-full">
            <p className="text-xs text-muted-foreground text-center mb-2">Link URL:</p>
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2.5">
              <p className="flex-1 font-mono text-xs truncate">
                {linkUrl}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="h-7 w-7 p-0 shrink-0"
              >
                {copied ? (
                  <CheckIcon className="size-3.5 text-emerald-500" />
                ) : (
                  <CopyIcon className="size-3.5" />
                )}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 grid grid-cols-3 gap-6 w-full text-center">
            <div>
              <p className="text-xl font-bold">{link.clickCount}</p>
              <p className="text-xs text-muted-foreground">Clicks</p>
            </div>
            <div>
              <p className="text-xl font-bold">{link.referralCount}</p>
              <p className="text-xs text-muted-foreground">Referrals</p>
            </div>
            <div>
              <p className="text-xl font-bold text-emerald-500">${link.totalEarnings.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Earned</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-center">
          <Button
            variant="outline"
            onClick={handleDownload}
            className="flex-1"
          >
            <DownloadIcon className="size-4 mr-2" />
            Download
          </Button>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

